import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, SectionCard, StatusBadge, TempBadge, Empty } from "@/components/shared";
import { inr, mt, fmtDate } from "@/lib/format";
import { Plus, Search, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    const params = {};
    if (q) params.q = q; if (stage) params.stage = stage;
    api.get("/projects", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, stage]);

  return (
    <div>
      <PageHeader title="Projects" subtitle="Master projects with duplicate detection and multi-partner attribution."
        actions={<button data-testid="register-project-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 text-white font-semibold px-4 py-2 rounded-lg copper-glow" style={{ background: "var(--copper)" }}><Plus size={16} /> Register Project</button>} />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="project-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project name or ID…" className="w-full bg-white border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm" />
        </div>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All Stages</option>
          {["Registered", "Qualified", "Quoted", "Negotiation", "Won", "Lost"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <SectionCard className="!p-0" testid="projects-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-5 py-3">Project ID</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">City</th>
              <th className="px-5 py-3">MT</th><th className="px-5 py-3">Value</th><th className="px-5 py-3">Lead</th>
              <th className="px-5 py-3">Stage</th><th className="px-5 py-3">Recs</th>
            </tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`project-row-${p.project_id}`} onClick={() => nav(`/projects/${p.id}`)} className="border-b border-slate-50 hover:bg-orange-50/30 cursor-pointer">
                  <td className="px-5 py-3 font-mono text-xs font-semibold text-slate-700">{p.project_id}</td>
                  <td className="px-5 py-3"><p className="font-medium text-slate-800">{p.name}</p><p className="text-xs text-slate-400">{p.building_type} · {p.builder_name}</p></td>
                  <td className="px-5 py-3 text-slate-600">{p.city}</td>
                  <td className="px-5 py-3 font-mono">{mt(p.tonnage_mt)}</td>
                  <td className="px-5 py-3 font-mono">{inr(p.est_value_inr)}</td>
                  <td className="px-5 py-3"><TempBadge temp={p.temperature} /></td>
                  <td className="px-5 py-3"><StatusBadge status={p.stage} /></td>
                  <td className="px-5 py-3 text-center font-mono">{p.recommendation_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <Empty text="No projects found." />}
        </div>
      </SectionCard>

      {open && <RegisterWizard onClose={() => setOpen(false)} onDone={(res) => { setOpen(false); load(); toast.success(res.outcome === "linked" ? "Linked as recommendation to existing master" : `Master project created: ${res.master.project_id}`); nav(`/projects/${res.master.id}`); }} />}
    </div>
  );
}

function RegisterWizard({ onClose, onDone }) {
  const [meta, setMeta] = useState({ building_types: [], stages: [] });
  const [partners, setPartners] = useState([]);
  const [f, setF] = useState({ name: "", city: "Mumbai", location: "", builder_name: "", site_contact_phone: "", partner_id: "", building_type: "Commercial", tonnage_mt: 5, est_value_inr: 5000000, hvac_stage: "Design", notes: "", force_new: false });
  const [dup, setDup] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/projects/meta").then((r) => setMeta(r.data));
    api.get("/partners", { params: { status: "verified" } }).then((r) => setPartners(r.data));
  }, []);

  useEffect(() => {
    if (!f.name || f.name.length < 3) { setDup(null); return; }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const { data } = await api.post("/projects/check-duplicate", { name: f.name, city: f.city, location: f.location, builder_name: f.builder_name, site_contact_phone: f.site_contact_phone });
        setDup(data);
      } catch {} finally { setChecking(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [f.name, f.city, f.location, f.builder_name, f.site_contact_phone]);

  const submit = async () => {
    if (!f.partner_id) { toast.error("Select the recommending partner"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/projects", { ...f, tonnage_mt: Number(f.tonnage_mt), est_value_inr: Number(f.est_value_inr) });
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="project-register-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">Register Project</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <F label="Project Name *"><input data-testid="proj-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="inp" /></F>
          <F label="City *"><select value={f.city} onChange={(e) => set("city", e.target.value)} className="inp">{["Mumbai","Delhi NCR","Bengaluru","Hyderabad","Chennai","Pune","Ahmedabad","Kolkata"].map((c) => <option key={c}>{c}</option>)}</select></F>
          <F label="Location / Area"><input value={f.location} onChange={(e) => set("location", e.target.value)} className="inp" /></F>
          <F label="Builder / Owner"><input value={f.builder_name} onChange={(e) => set("builder_name", e.target.value)} className="inp" /></F>
          <F label="Site Contact Phone"><input value={f.site_contact_phone} onChange={(e) => set("site_contact_phone", e.target.value)} className="inp" /></F>
          <F label="Building Type"><select value={f.building_type} onChange={(e) => set("building_type", e.target.value)} className="inp">{meta.building_types.map((b) => <option key={b}>{b}</option>)}</select></F>
          <F label="Tonnage (MT)"><input type="number" data-testid="proj-mt" value={f.tonnage_mt} onChange={(e) => set("tonnage_mt", e.target.value)} className="inp" /></F>
          <F label="Estimated Value (₹)"><input type="number" value={f.est_value_inr} onChange={(e) => set("est_value_inr", e.target.value)} className="inp" /></F>
          <F label="HVAC Stage"><select value={f.hvac_stage} onChange={(e) => set("hvac_stage", e.target.value)} className="inp">{["Design","Tender","Execution"].map((s) => <option key={s}>{s}</option>)}</select></F>
          <F label="Recommending Partner *"><select data-testid="proj-partner" value={f.partner_id} onChange={(e) => set("partner_id", e.target.value)} className="inp"><option value="">Select partner…</option>{partners.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.partner_id}</option>)}</select></F>

          {/* Duplicate engine panel */}
          <div className="sm:col-span-2" data-testid="duplicate-detection-panel">
            {checking && <p className="text-xs text-slate-400">Checking for duplicates…</p>}
            {dup && dup.matches.length > 0 && (
              <div className={`rounded-lg border p-4 ${dup.is_duplicate ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={16} className={dup.is_duplicate ? "text-red-600" : "text-amber-600"} />
                  <p className="text-sm font-semibold text-slate-800">{dup.is_duplicate ? "Likely duplicate detected" : "Possible related projects"}</p>
                  <span className="ml-auto font-mono text-sm font-bold" style={{ color: dup.is_duplicate ? "#DC2626" : "#D97706" }}>{dup.top_confidence}% match</span>
                </div>
                {dup.matches.slice(0, 2).map((m) => (
                  <div key={m.master.id} className="bg-white rounded-lg p-3 mb-2 border border-slate-100">
                    <div className="flex justify-between items-center">
                      <div><p className="text-sm font-medium">{m.master.name}</p><p className="text-xs font-mono text-slate-400">{m.master.project_id}</p></div>
                      <span className="font-mono text-sm font-bold text-slate-700">{m.confidence}%</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {m.breakdown.map((b) => <span key={b.field} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{b.field}: {b.match}%</span>)}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-slate-600 mt-1">{dup.is_duplicate ? "On submit, this will link as a recommendation to the top master (first recommender keeps payout)." : "Submit will create a new master unless you match above threshold."}</p>
                <label className="flex items-center gap-2 text-xs text-slate-600 mt-2"><input type="checkbox" checked={f.force_new} onChange={(e) => set("force_new", e.target.checked)} /> Force create as new master project</label>
              </div>
            )}
            {dup && dup.matches.length === 0 && f.name.length > 2 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 size={15} /> No duplicates — a new master project will be created.</div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button data-testid="proj-submit" disabled={busy || !f.name || !f.partner_id} onClick={submit} className="px-5 py-2 text-sm text-white font-semibold rounded-lg copper-glow disabled:opacity-50" style={{ background: "var(--copper)" }}>{busy ? "Registering…" : "Register & Generate ID"}</button>
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid #CBD5E1;border-radius:8px;padding:8px 10px;font-size:14px;background:#fff}.inp:focus{outline:none;border-color:var(--copper)}`}</style>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>{children}</div>;
}
